import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { campanhasService } from './services/campanhasService';
import { convitesService } from './services/convitesService';
import { escritasAxiisService } from './services/escritasAxiisService';
import { painelService } from './services/painelService';
import { sincronizacaoService } from './services/sincronizacaoService';
import type { FiltrosPainel, Idioma } from './types/Painel';

export const campanhasQueries = {
  all: () => ['campanhas'] as const,
  lista: () =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'lista'] as const,
      queryFn: () => campanhasService.listar(),
    }),
  nomePublico: (id: string) =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), id, 'nome-publico'] as const,
      queryFn: () => campanhasService.nomePublico(id),
      staleTime: 300_000, // o mesmo `max-age` da rota
      retry: false,
    }),
  detalhe: (id: string) =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'detalhe', id] as const,
      queryFn: () => campanhasService.obter(id),
      staleTime: 0, // spec 20 R6a
    }),
  progresso: (id: string) =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), id, 'progresso'] as const,
      queryFn: () => campanhasService.progresso(id),
      staleTime: 0,
      refetchOnWindowFocus: true,
    }),
  /** Sob `campanhas`: toda invalidação de `['campanhas']` a refaz (spec 29 R19). */
  progressoTodas: () =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'progresso'] as const,
      queryFn: () => campanhasService.progressoTodas(),
      staleTime: 0,
    }),
  /** Refeita ao abrir o diálogo de Abrir (spec 28 R18). */
  ativa: () =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'ativa'] as const,
      queryFn: () => campanhasService.ativa(),
      staleTime: 0,
    }),
};

export const sincronizacaoQueries = {
  all: (id: string) => [...campanhasQueries.all(), 'sincronizacao', id] as const,
  ultima: (id: string) =>
    queryOptions({
      queryKey: [...sincronizacaoQueries.all(id), 'ultima'] as const,
      queryFn: () => sincronizacaoService.ultima(id),
    }),
};

export const escritasAxiisQueries = {
  resumo: (id: string) =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'escritas-axiis', id] as const,
      queryFn: () => escritasAxiisService.resumo(id),
    }),
};

export const painelQueries = {
  all: (id: string) => [...campanhasQueries.all(), 'painel', id] as const,
  lista: (id: string, filtros: FiltrosPainel) =>
    queryOptions({
      queryKey: [...painelQueries.all(id), 'lista', filtros] as const,
      queryFn: () => painelService.listar(id, filtros),
      staleTime: 0, // spec 20 R6a
      placeholderData: keepPreviousData,
    }),
  detalhe: (id: string, participanteId: string) =>
    queryOptions({
      queryKey: [...painelQueries.all(id), 'detalhe', participanteId] as const,
      queryFn: () => painelService.detalhe(id, participanteId),
      staleTime: 0, // spec 20 R6a
    }),
  /** Seletor da inclusão manual: o debounce fica no `queryFn` (a chave só muda com a busca). */
  candidatos: (id: string, busca: string) =>
    queryOptions({
      queryKey: [...painelQueries.all(id), 'candidatos', busca] as const,
      queryFn: async ({ signal }) => {
        await new Promise((r) => setTimeout(r, 300));
        if (signal.aborted) throw new DOMException('aborted', 'AbortError');
        return painelService.candidatos(id, busca);
      },
      enabled: busca.length >= 2,
      staleTime: 0,
    }),
};

export const convitesQueries = {
  previa: (id: string, participanteId?: string, idioma?: Idioma) =>
    queryOptions({
      queryKey: [
        ...campanhasQueries.all(),
        'convites',
        id,
        'previa',
        participanteId ?? null,
        idioma ?? null,
      ] as const,
      queryFn: () => convitesService.previa(id, participanteId, idioma),
      staleTime: 0,
    }),
  /** Sob o prefixo de `previa` que o `useSalvarModelo` invalida (spec 30 R11). */
  previaRascunho: (id: string, idioma: Idioma, rascunho: { assunto: string; corpo: string }) =>
    queryOptions({
      queryKey: [
        ...campanhasQueries.all(),
        'convites',
        id,
        'previa',
        'rascunho',
        idioma,
        rascunho,
      ] as const,
      queryFn: () => convitesService.previaRascunho(id, { idioma, modelo: rascunho }),
      retry: false,
      staleTime: 0,
    }),
  modelos: (id: string) =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'convites', id, 'modelos'] as const,
      queryFn: () => convitesService.modelos(id),
    }),
  resumo: (id: string) =>
    queryOptions({
      queryKey: [...campanhasQueries.all(), 'convites', id] as const,
      queryFn: async ({ client }) => {
        const anterior = client.getQueryData(convitesQueries.resumo(id).queryKey);
        const novo = await convitesService.resumo(id);
        // Rodada terminou: a lista de participantes (coluna Convite) está defasada.
        if (
          anterior?.ultimaRodada?.status === 'em_andamento' &&
          novo.ultimaRodada?.status !== 'em_andamento'
        ) {
          void client.invalidateQueries({ queryKey: painelQueries.all(id) });
          void client.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey });
        }
        return novo;
      },
      refetchInterval: (query) =>
        query.state.data?.ultimaRodada?.status === 'em_andamento' ? 1000 : false,
    }),
};
